import { useCallback, useState } from "react";
import {
  markAsDelivered,
  markAsReadyForDelivery,
  refundOrder,
  returnOrder,
  capturePayment,
} from "@/api/order-api";
import { OrderResponse } from "@/types/order-type";
import { PaymentDialogValues } from "@/components/orders/PaymentDialog";
import { AdjustmentMode } from "@/components/orders/OrderAdjustmentDialog";
import { ApiResponse } from "@/types/api-response";
import { toast } from "@/hooks/use-toast";

interface UseOrderDetailsActionsReturn {
  onReadyForDelivery: (order: OrderResponse) => Promise<void>;
  onDelivered: (order: OrderResponse) => Promise<void>;
  handleAdjustmentSubmit: (payload: {
    mode: AdjustmentMode;
    amount?: number;
    reason: string;
    restock?: boolean;
  }) => Promise<void>;
  handleConfirmPayment: (payload: PaymentDialogValues) => Promise<void>;
  submitting: boolean;
}

export function useOrderDetailsActions(
  orderId: string | null,
  onRefresh: () => void
): UseOrderDetailsActionsReturn {
  const [submitting, setSubmitting] = useState(false);

  const onReadyForDelivery = useCallback(
    async (order: OrderResponse) => {
      try {
        const resp = await markAsReadyForDelivery(order.id);
        if (resp.success) {
          toast({
            title: "Success",
            description:
              "Order has been marked as ready for delivery successfully",
          });
          onRefresh();
        } else {
          toast({
            title: "Error",
            description:
              resp.message ||
              "Failed to change order delivery status. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to change order delivery status. Please try again.",
          variant: "destructive",
        });
      }
    },
    [onRefresh]
  );

  const onDelivered = useCallback(
    async (order: OrderResponse) => {
      if (!order) return;
      try {
        const resp = await markAsDelivered(order.id);
        if (resp.success) {
          toast({
            title: "Success",
            description: "Order has been marked as delivered successfully",
          });
          onRefresh();
        } else {
          toast({
            title: "Error",
            description:
              resp.message ||
              "Failed to change order delivery status. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to change order delivery status. Please try again.",
          variant: "destructive",
        });
      }
    },
    [onRefresh]
  );

  const handleAdjustmentSubmit = useCallback(
    async (payload: {
      mode: AdjustmentMode;
      amount?: number;
      reason: string;
      restock?: boolean;
    }) => {
      if (!orderId) return;

      setSubmitting(true);
      try {
        let resp: ApiResponse<{}> | null = null;
        if (payload.mode === "REFUND") {
          resp = await refundOrder(orderId, payload.amount!, payload.reason!);
        } else {
          resp = await returnOrder(orderId, payload.restock!, payload.reason!);
        }
        if (resp.success) {
          toast({
            title: "Success",
            description: "Order has been refunded successfully",
          });
          onRefresh();
        } else {
          throw new Error(resp.message);
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to make order adjustment. Please try again.",
          variant: "destructive",
        });
        throw error;
      } finally {
        setSubmitting(false);
      }
    },
    [orderId, onRefresh]
  );

  const handleConfirmPayment = useCallback(
    async (payload: PaymentDialogValues) => {
      if (!orderId) return;

      try {
        const resp = await capturePayment(orderId, payload);
        if (resp.success) {
          toast({
            title: "Success",
            description: "Order has been captured payment successfully",
          });
        } else {
          toast({
            title: "Error",
            description:
              resp.message || "Failed to capture payment. Please try again.",
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to capture payment. Please try again.",
          variant: "destructive",
        });
      } finally {
        onRefresh();
      }
    },
    [orderId, onRefresh]
  );

  return {
    onReadyForDelivery,
    onDelivered,
    handleAdjustmentSubmit,
    handleConfirmPayment,
    submitting,
  };
}
